// API service for connecting to the QA Judge backend

import type { JudgeResultData, JudgeSkipReason } from '@/lib/api-v2';
import type { DatabaseEnv } from './environment-context';

const envVars = import.meta.env as unknown as { VITE_QA_JUDGE_URL?: string };

// In production builds, use relative URLs (goes through nginx proxy at same origin)
// In development, use explicit backend URL or fallback to localhost:3001
const isLocal = import.meta.env.DEV;
const API_BASE = isLocal ? envVars.VITE_QA_JUDGE_URL || 'http://localhost:3001' : '';

// Types based on your backend structure
export interface JudgeResult {
  score: number;
  summary: string;
  violations: string[];
  evidence: string[];
  suggestions?: string[];
  improvedQueries?: Array<{
    query: string;
    filter?: string | null;
    facet?: string | string[] | null;
    searchFields?: string[] | null;
    notes?: string;
  }>;
}

export interface EvaluationResult {
  sessionId?: string;
  batchId?: string;
  file: string;
  evaluatedAt: string;
  title?: string;
  rule_following: JudgeResult;
  conversation_quality: JudgeResult;
  hallucination?: JudgeResult;
  azure_search?: JudgeResult;
}

export interface SessionLog {
  sessionId: string;
  filePath: string;
  fileName: string;
  size: number;
  modifiedAt: string;
  eventCount: number;
  hasSystemPrompt: boolean;
  title?: string;
}

export interface LogsResponse {
  logs: SessionLog[];
  total: number;
  logsDir: string;
}

export interface ChatAndJudgeRequest {
  message: string;
  firstName?: string;
  userId?: string;
  env?: DatabaseEnv;
}

export interface ChatAndJudgeResponse {
  sessionId: string;
  chatResult: {
    response: unknown;
    fullContent: string;
  };
  judgeResult: EvaluationResult;
}

export interface TestResultsListResponse {
  results: Array<{
    sessionId: string;
    batchId?: string;
    filePath: string;
    fileName: string;
    evaluatedAt: string;
    title?: string;
    ruleScore?: number;
    qualityScore?: number;
    hallucinationScore?: number;
    avg10?: number;
  }>;
  total: number;
  dir: string;
}

export interface ContinueAndJudgeRequest {
  sessionId: string;
  message: string;
  firstName?: string;
  env?: DatabaseEnv;
}

export interface JudgeRequest {
  sessionId: string;
}

export interface AzureAnalyticsResponse {
  sessions: Array<{
    sessionId: string;
    tags?: Record<string, string>;
    metadata?: Record<string, string>;
    lastModified?: string;
    contentLength?: number;
  }>;
  evaluations: Record<string, EvaluationResult>;
  total: number;
  query: string;
}

export interface AzureLatestItem {
  name: string;
  sessionId: string;
  lastModified?: string;
  contentLength?: number;
  tags?: Record<string, string>;
  metadata?: Record<string, string>;
}

export interface UserInfo {
  id: string;
  email: string;
  fullName: string;
}

export type SubscriptionTier =
  | 'Pro'
  | 'Core'
  | 'Pro (Manual)'
  | 'Core (Manual)'
  | 'Team'
  | 'Admin'
  | 'Starter'
  | 'Unknown';

// Credit Analytics types
export interface CreditUsageSummary {
  usage: Record<string, { count: number; totalCredits: number }>;
  totalSpent: number;
  totalGranted: number;
}

export interface CreditEvent {
  id: string;
  userId: string;
  eventType: string;
  amount: number;
  balanceAfter: number;
  reason: string;
  maxBalanceInPeriod: number;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface CreditEventHistory {
  events: CreditEvent[];
  total: number;
}

export interface BulkCreditUsageEntry {
  userId: string;
  clerkUserId?: string | null;
  email?: string | null;
  // Last active timestamp sourced from the gieni-user-backend User entity.
  lastActiveAt?: string | null;
  usage: Record<string, { count: number; totalCredits: number }>;
  totalSpent: number;
  totalGranted: number;
}

export interface PlanHistoryEntry {
  date: string;
  previousRole: string | null;
  newRole: string;
  reason: string;
}

export interface UserBillingInfo {
  userId: string;
  tier: SubscriptionTier;
  creditBalance: number;
  features: {
    chat: boolean;
    reports: boolean;
    companies_export: boolean;
    data_explorer: boolean;
    contact: boolean;
    pipeline: boolean;
  };
}

/** Billing info extended with detail-only fields returned by the
 * single-user billing endpoint. */
export interface UserBillingDetails extends UserBillingInfo {
  billingPeriod: 'monthly' | 'yearly' | null;
  organizationRole: 'admin' | 'member' | null;
}

// Stripe Subscription Types
export interface StripeSubscriptionOverview {
  mrr: number;
  mrrFormatted: string;
  activeSubs: number;
  /** Active subscriptions with a scheduled cancellation date (still active today) */
  cancelationRequestedSubs: number;
  trialingSubs: number;
  pastDueSubs: number;
  canceledLast30: number;
  churnRate: number;
  /** Human-readable description of how churnRate is computed (rendered in a tooltip) */
  churnMethodology: string;
  downgradeRates: {
    coreToStarter: number;
    proToCoreOrStarter: number;
    methodology: string;
  };
  trialConversionRate: number;
  statusBreakdown: { status: string; count: number }[];
  /** ISO 4217 currency code from Stripe (e.g. "eur") */
  currency: string;
}

export interface StripeRecentChange {
  customerId: string;
  customerEmail: string | null;
  customerName: string | null;
  eventType:
    | 'created'
    | 'upgraded'
    | 'downgraded'
    | 'canceled'
    | 'cancel_scheduled'
    | 'reactivated'
    | 'trial_started'
    | 'trial_ended';
  planName: string | null;
  planTier: SubscriptionTier;
  mrrImpact: number;
  timestamp: string;
}

export interface StripeUserSubscription {
  subscriptionId: string;
  status: string;
  planName: string | null;
  planTier: SubscriptionTier;
  planAmount: number;
  planInterval: string;
  currency: string;
  mrrContribution: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  trialStart: string | null;
  trialEnd: string | null;
  created: string;
}

// PostHog Event Analytics Types

/** Deployment environment an event can be filtered to (matches backend). */
export type PosthogEnvironment = 'production' | 'staging' | 'development';

export interface PosthogEventBreakdown {
  event: string;
  count: number;
  pct: number;
}

export interface PosthogEventTrend {
  day: string;
  event: string;
  count: number;
}

export interface PosthogEventOverview {
  totalEvents: number;
  uniqueUsers: number;
  breakdown: PosthogEventBreakdown[];
  trends: PosthogEventTrend[];
  period: string;
}

export interface PosthogUserEventSummary {
  distinctId: string;
  totalEvents: number;
  breakdown: { event: string; count: number }[];
}

export interface PosthogDailyUserActivity {
  day: string;
  distinctId: string;
  count: number;
}

export interface PosthogChatModeBreakdown {
  mode: string;
  count: number;
  pct: number;
  uniqueUsers: number;
}

export interface PosthogChatModeOverview {
  total: number;
  breakdown: PosthogChatModeBreakdown[];
  period: string;
}

// Clerk User Lookup Types
export interface ClerkUser {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string;
  lastActiveAt: string | null;
  lastSignInAt: string | null;
  createdAt: string | null;
  imageUrl: string | null;
}

export interface ClerkEnvironmentUsers {
  available: boolean;
  users: ClerkUser[];
  count: number;
  totalCount: number; // Total before pagination
  activeCount: number; // Total active users (with lastActiveAt)
}

export interface ClerkPagination {
  limit: number;
  offset: number;
  activeOnly: boolean;
  sortBy?: 'last_active_at' | 'created_at' | 'email';
  sortDir?: 'asc' | 'desc';
}

// New response format: returns only the allowed environment based on deployment mode
export interface ClerkUsersResponse {
  environment: 'development' | 'staging' | 'production';
  available: boolean;
  users: ClerkUser[];
  count: number;
  totalCount: number;
  activeCount: number;
  filteredCount: number;
  pagination: ClerkPagination;
}

// User Sessions Types
export interface UserSessionTurn {
  turnNumber: number;
  userMessage: string;
  timestamp: string;
}

export interface UserSession {
  id: string;
  userId: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  turnCount: number;
  turns: UserSessionTurn[];
}

export interface UserSessionsResponse {
  userId: string;
  sessions: UserSession[];
  hasMore: boolean;
  nextCursor: string | null;
  totalCount: number;
}

// User Profile (DB-backed, replaces Mem0 memories)
export interface UserProfile {
  id: string;
  userId: string;
  name: string | null;
  email: string | null;
  role: string | null;
  company: string | null;
  companyType: string | null;
  companyWebsite: string | null;
  location: string | null;
  industry: string | null;
  objectives: string[] | null;
  createdAt: string;
  updatedAt: string;
  profileCompletionRemindedAt: string | null;
}

// Mem0 Memory Types
export interface Mem0Memory {
  id: string;
  memory: string;
  created_at: string;
  updated_at: string;
}

export interface Mem0MemoriesResponse {
  memories: Mem0Memory[];
  page: number;
  page_size: number;
}

// User Location from Clerk Session
export interface UserLocation {
  city: string | null;
  country: string | null;
  ipAddress: string | null;
  deviceType: string | null;
  browserName: string | null;
  browserVersion: string | null;
  isMobile: boolean | null;
}

export interface AzureDistinctResponse {
  distinct: Record<string, string[]>;
  users?: UserInfo[]; // Enhanced user information with names
}

// Dashboard Stats Types
export interface DashboardStatsSessionByDate {
  date: string;
  count: number;
}

export interface DashboardStatsSessionByUser {
  userId: string;
  email: string;
  fullName: string;
  count: number;
}

export interface DashboardStatsActiveUser {
  userId: string;
  email: string;
  fullName: string;
  lastActiveAt: string | null;
  imageUrl: string | null;
}

export interface DashboardStatsResponse {
  totalSessions: number;
  uniqueUsers: number;
  dau: number;
  wau: number;
  mostRecentDate: string | null;
  sessionsByDate: DashboardStatsSessionByDate[];
  // Distinct users who created at least one new session per date bucket.
  // Hourly buckets (usersByDateGranularity === 'hour') use full ISO
  // timestamps; daily buckets use YYYY-MM-DD.
  usersByDate: DashboardStatsSessionByDate[];
  usersByDateGranularity?: 'day' | 'hour';
  sessionsByUser: DashboardStatsSessionByUser[];
  activeUsers: DashboardStatsActiveUser[];
  // User growth metrics
  userSignupsByDate: DashboardStatsSessionByDate[];
  userActivityByDate: DashboardStatsSessionByDate[];
  // Session depth metrics
  avgTurnsPerSession: number;
  avgSessionDurationMs: number | null;
  granularity: 'day' | 'week';
  env: string;
  // Cache metadata
  computedAt: string;
  cached: boolean;
  cacheAge?: number; // ms since computation (only present if cached=true)
}

export interface DashboardStatsOptions {
  env?: DatabaseEnv;
  granularity?: 'day' | 'week';
  days?: number;
  weeks?: number;
  userLimit?: number;
  refresh?: boolean; // Force recomputation (ignore cache)
  filters?: {
    userId?: string;
    userIds?: string[]; // Filter by multiple user IDs (for domain filter)
    excludeUserIds?: string[]; // Exclude specific user IDs (for exclude internal)
    dateFrom?: string; // ISO date string
    dateTo?: string; // ISO date string
    excludeOrderfox?: boolean;
  };
}

// Stickiness stats types
export interface StickinessRatioPoint {
  date: string;
  dau: number;
  wau: number;
  ratio: number;
}

export interface ActivityFrequencyBucket {
  activeDays: number;
  userCount: number;
  userIds: string[];
}

export interface StreakDistributionBucket {
  streakDays: number;
  userCount: number;
  userIds: string[];
}

export interface StickinessStatsResponse {
  stickinessRatio: StickinessRatioPoint[];
  activityFrequency: ActivityFrequencyBucket[];
  streakDistribution: StreakDistributionBucket[];
  users: UserInfo[];
  env: string;
  computedAt: string;
}

export interface StickinessStatsOptions {
  env?: DatabaseEnv;
  days?: number;
  filters?: {
    userId?: string;
    userIds?: string[];
    excludeUserIds?: string[];
    dateFrom?: string;
    dateTo?: string;
    excludeOrderfox?: boolean;
  };
}

// Retention breakdown types
export interface RetentionCategoryStat {
  label: string;
  returningUsers: number;
  returningSessions: number;
}

export interface RetentionBreakdownResponse {
  totalReturningUsers: number;
  totalReturningSessions: number;
  // Distinct returning users per date bucket, zero-filled across the window.
  // Hourly buckets (granularity === 'hour') use full ISO timestamps; daily
  // buckets use YYYY-MM-DD.
  returningUsersByDate: DashboardStatsSessionByDate[];
  returningUsersByDateGranularity: 'day' | 'hour';
  dimensions: {
    industry: RetentionCategoryStat[];
    country: RetentionCategoryStat[];
    machineType: RetentionCategoryStat[];
  };
  env: string;
  computedAt: string;
}

export type RetentionBreakdownOptions = StickinessStatsOptions;

// Pagination types
export interface PaginationMeta {
  pageSize: number;
  hasMore: boolean;
  cursor: string | null;
  sortDir: 'asc' | 'desc';
}

export interface PaginationLinks {
  self: string;
  first: string;
  next?: string;
}

export interface PaginatedSessionsResponse {
  items: AzureLatestItem[];
  pagination: PaginationMeta;
  links: PaginationLinks;
  total: number;
}

// Report Wizard types
export interface ResearchParameter {
  id: string;
  category: string;
  title: string;
  fields: string[];
  icon?: string;
}

export interface ReportFocus {
  reportType: string;
  focusSummary: string;
}

export interface ReportSection {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  order: number;
}

export interface ReportStructure {
  title: string;
  reportDepthAndTone: 'brief' | 'standard' | 'deep';
  language: string;
  sections: ReportSection[];
}

export interface WizardContent {
  researchParameters: ResearchParameter[];
  reportFocus?: ReportFocus;
}

export interface ReportWizardListItem {
  id: string;
  sessionId: string;
  userId: string;
  status: string;
  title: string | null;
  sessionTitle: string | null;
  reportType: string | null;
  reportDepthAndTone: string | null;
  language: string | null;
  sectionCount: number;
  researchParameterCount: number;
  turnCount: number;
  createdAt: string;
  updatedAt: string;
  generatedAt: string | null;
  tags: { user: string; userId: string };
  metadata: { userFullName?: string };
}

export interface ReportWizardDetail extends ReportWizardListItem {
  content: WizardContent | null;
  structure: ReportStructure | null;
}

export interface PaginatedReportWizardsResponse {
  items: ReportWizardListItem[];
  pagination: PaginationMeta;
  links: PaginationLinks;
  total: number;
}

export interface TurnDashboard {
  id: string;
  turnNumber: number;
  userMessage: string;
  assistantMessage: string;
  dashboard: unknown;
  startAt: string;
  endAt: string | null;
  durationMs: number | null;
}

export interface SessionDashboardsResponse {
  sessionId: string;
  turns: TurnDashboard[];
  count: number;
}

// Data Explorer datasets (sessions DB, read-only mirror)
export interface SessionDataset {
  id: string;
  turnId: string;
  userId: string | null;
  title: string | null;
  companyCount: number;
  contactCount: number;
  machineCount: number;
  employeeCount: number;
  maxEmployeeCount: number | null;
  maxMachineCount: number | null;
  mapType: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | string;
  viewed: boolean;
  imageFileName: string | null;
  imageMimeType: string | null;
  isSample: boolean;
  isArchived: boolean;
  archivedAt: string | null;
  originalDatasetId: string | null;
  searchIndexName: string;
  createdAt: string;
  updatedAt: string;
}

export interface SessionDatasetsResponse {
  sessionId: string;
  datasets: SessionDataset[];
  count: number;
}

// Data exports: CSV/XLSX downloads and CRM pushes (sessions DB, read-only mirror)
export interface SessionDataExport {
  id: string;
  createdAt: string;
  userId: string;
  source: 'SESSION' | 'DATASET' | string;
  exportTarget: 'FILE' | 'CRM' | string;
  fileType: 'CSV' | 'XLSX' | null;
  endIndex: number | null;
  companyCount: number;
  searchIndexName: string;
  datasetId: string | null;
  sessionId: string | null;
  turnId: string | null;
}

export interface SessionDataExportsResponse {
  sessionId: string;
  dataExports: SessionDataExport[];
  count: number;
}

// Session reports (gieni-user-backend internal reports API proxy)
export interface SessionReport {
  id: string;
  jobId?: string;
  title?: string;
  status: 'completed' | 'generating';
  /** ISO timestamp the report was created (used to place it under its turn). */
  createdAt?: string;
  /** Proxy download path, present only when status === 'completed'. */
  downloadUrl?: string;
  /** When present, the report was produced by a schedule; otherwise on-demand. */
  scheduleId?: string;
}

// Session dashboard presentations (gieni-user-backend internal reports API proxy, PPTX)
export interface SessionDashboardPresentation {
  id: string;
  jobId?: string;
  title?: string;
  turnNumber?: number;
  status: 'completed' | 'generating';
  /** Proxy download path, present only when status === 'completed'. */
  downloadUrl?: string;
}

// Report Pipeline Job types (gieni-reports Azure Function)
export interface ReportPipelineJob {
  job_id: string;
  azure_job_execution_id: string;
  status: 'Succeeded' | 'Failed' | 'Running' | 'Stopped' | 'Cancelled' | string;
  start_time: string;
  end_time: string | null;
  topic: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  requested_at: string;
  previous_execution_ids: string[];
  pdf_url: string | null;
  snippet_pdf_url: string | null;
  preview_image_url: string | null;
  title: string | null;
}

export interface ReportPipelineJobsResponse {
  success: boolean;
  jobs: ReportPipelineJob[];
  total?: number;
  limit?: number;
  offset?: number;
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit & { timeoutMs?: number } = {}
  ): Promise<T> {
    const url = `${API_BASE}${endpoint}`;
    const controller = new AbortController();
    const timeoutMs = (options as { timeoutMs?: number }).timeoutMs ?? 15000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const { timeoutMs: _omitTimeoutMs, ...fetchOptions } = options;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(fetchOptions.headers || {}),
        },
        signal: controller.signal,
        ...fetchOptions,
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
          if (errorData.details) {
            errorMessage += `\n\nDetails:\n${errorData.details}`;
          }
        } catch {
          // If parsing error body fails, use default message
        }
        throw new ApiError(response.status, errorMessage);
      }

      if (response.status === 204) {
        // No Content
        return undefined as unknown as T;
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        return (await response.json()) as T;
      }
      // Fallback to text for non-JSON responses
      return (await response.text()) as unknown as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if ((error as { name?: string })?.name === 'AbortError') {
        throw new Error('Network timeout: request took too long');
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Health check
  async getHealth(): Promise<{ status: string; service: string; timestamp: string }> {
    return this.request<{ status: string; service: string; timestamp: string }>('/health');
  }

  // Chat and Judge Methods (work with Azure sessions)
  async chatAndJudge(data: ChatAndJudgeRequest): Promise<ChatAndJudgeResponse> {
    const { env, ...body } = data;
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    const queryString = params.toString();
    return this.request<ChatAndJudgeResponse>(
      `/v1/chat-and-judge${queryString ? `?${queryString}` : ''}`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        timeoutMs: 300000,
      }
    );
  }

  async continueAndJudge(data: ContinueAndJudgeRequest): Promise<ChatAndJudgeResponse> {
    const { env, ...body } = data;
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    const queryString = params.toString();
    return this.request<ChatAndJudgeResponse>(
      `/v1/continue-and-judge${queryString ? `?${queryString}` : ''}`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        timeoutMs: 300000,
      }
    );
  }

  async getEnvironments(): Promise<{
    environments: Array<{ env: string; label: string; available: boolean }>;
    deploymentMode: 'dev' | 'prod';
  }> {
    return this.request<{
      environments: Array<{ env: string; label: string; available: boolean }>;
      deploymentMode: 'dev' | 'prod';
    }>(`/v1/azure/environments`);
  }

  async getAzureTotalCount(env?: string): Promise<{ total: number; cached: boolean }> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<{ total: number; cached: boolean }>(
      `/v1/azure/logs/count?${params.toString()}`
    );
  }

  async getAzureDistinctTags(env?: string): Promise<AzureDistinctResponse> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<AzureDistinctResponse>(`/v1/azure/tags/distinct?${params.toString()}`);
  }

  async getAzureSession(sessionId: string, trim?: number, env?: DatabaseEnv): Promise<unknown> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    if (trim && trim > 0) params.append('trim', String(trim));
    const queryString = params.toString();
    const url = `/v1/azure/logs/${encodeURIComponent(sessionId)}${queryString ? `?${queryString}` : ''}`;
    return this.request<unknown>(url);
  }

  async judgeAzureSession(
    sessionId: string,
    overwrite: boolean = false,
    env?: DatabaseEnv
  ): Promise<EvaluationResult> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    if (overwrite) params.append('overwrite', 'true');
    const queryString = params.toString();
    const url = `/v1/azure/logs/${encodeURIComponent(sessionId)}/judge${queryString ? `?${queryString}` : ''}`;
    return this.request<EvaluationResult>(url, {
      method: 'POST',
      timeoutMs: 300000,
    });
  }

  async importAzureSession(
    sessionId: string
  ): Promise<{ ok: boolean; path: string; size: number; eventCount: number }> {
    return this.request<{ ok: boolean; path: string; size: number; eventCount: number }>(
      `/v1/azure/logs/${encodeURIComponent(sessionId)}/import`,
      {
        method: 'POST',
        timeoutMs: 30000,
      }
    );
  }

  async getAzureEvaluation(sessionId: string, env?: DatabaseEnv): Promise<EvaluationResult | null> {
    try {
      const params = new URLSearchParams();
      if (env) params.append('env', env);
      const queryString = params.toString();
      return await this.request<EvaluationResult>(
        `/v1/azure/evaluations/${encodeURIComponent(sessionId)}${queryString ? `?${queryString}` : ''}`
      );
    } catch (error) {
      // Return null if evaluation doesn't exist (404)
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getAzureEvaluations(
    sessionIds: string[],
    env?: DatabaseEnv
  ): Promise<Record<string, EvaluationResult>> {
    if (sessionIds.length === 0) {
      return {};
    }
    const params = new URLSearchParams();
    params.append('sessionIds', sessionIds.join(','));
    if (env) params.append('env', env);
    const response = await this.request<{ results: Record<string, EvaluationResult> }>(
      `/v1/azure/evaluations?${params.toString()}`
    );
    return response.results || {};
  }

  async deleteAzureSession(
    sessionId: string,
    env?: DatabaseEnv
  ): Promise<{ ok: boolean; deleted: unknown }> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    const queryString = params.toString();
    return this.request<{ ok: boolean; deleted: unknown }>(
      `/v1/azure/logs/${encodeURIComponent(sessionId)}${queryString ? `?${queryString}` : ''}`,
      {
        method: 'DELETE',
        timeoutMs: 15000,
      }
    );
  }

  async setAzureSessionTitle(
    sessionId: string,
    title: string,
    env?: DatabaseEnv
  ): Promise<{ ok: boolean; title: string }> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    const queryString = params.toString();
    return this.request<{ ok: boolean; title: string }>(
      `/v1/azure/logs/${encodeURIComponent(sessionId)}/title${queryString ? `?${queryString}` : ''}`,
      {
        method: 'POST',
        body: JSON.stringify({ title }),
      }
    );
  }

  async getAzureAnalytics(
    filters?: Record<string, string>,
    limit?: number
  ): Promise<AzureAnalyticsResponse> {
    return this.request<AzureAnalyticsResponse>('/v1/azure/analytics', {
      method: 'POST',
      body: JSON.stringify({ filters, limit }),
      timeoutMs: 60000,
    });
  }

  /**
   * Get dashboard statistics from SQL database
   * @param options - Query options for environment, granularity, and time range
   * @returns Dashboard statistics including total sessions, user counts, and timeline data
   */
  async getDashboardStats(options: DashboardStatsOptions = {}): Promise<DashboardStatsResponse> {
    const {
      env = 'prod',
      granularity = 'day',
      days,
      weeks,
      userLimit = 15,
      refresh,
      filters,
    } = options;
    const params = new URLSearchParams();
    params.append('env', env);
    params.append('granularity', granularity);
    // Only send the relevant time parameter based on granularity
    if (granularity === 'week') {
      params.append('weeks', String(weeks ?? 8));
    } else {
      params.append('days', String(days ?? 30));
    }
    params.append('userLimit', String(userLimit));
    if (refresh) {
      params.append('refresh', 'true');
    }
    if (filters) {
      if (filters.userId) {
        params.append('userId', filters.userId);
      }
      // Send userIds as comma-separated string for domain filtering
      if (filters.userIds && filters.userIds.length > 0) {
        params.append('userIds', filters.userIds.join(','));
      }
      // Send excludeUserIds as comma-separated string for excluding internal users
      if (filters.excludeUserIds && filters.excludeUserIds.length > 0) {
        params.append('excludeUserIds', filters.excludeUserIds.join(','));
      }
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo);
      }
      if (filters.excludeOrderfox) {
        params.append('excludeOrderfox', 'true');
      }
    }
    return this.request<DashboardStatsResponse>(`/v1/azure/dashboard-stats?${params.toString()}`);
  }

  /**
   * Get stickiness statistics (DAU/WAU ratio, activity frequency, streak distribution)
   */
  async getStickinessStats(options: StickinessStatsOptions = {}): Promise<StickinessStatsResponse> {
    const { env = 'prod', days, filters } = options;
    const params = new URLSearchParams();
    params.append('env', env);
    if (days) params.append('days', String(days));
    if (filters) {
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.userIds && filters.userIds.length > 0) {
        params.append('userIds', filters.userIds.join(','));
      }
      if (filters.excludeUserIds && filters.excludeUserIds.length > 0) {
        params.append('excludeUserIds', filters.excludeUserIds.join(','));
      }
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.excludeOrderfox) params.append('excludeOrderfox', 'true');
    }
    return this.request<StickinessStatsResponse>(`/v1/azure/stickiness-stats?${params.toString()}`);
  }

  /**
   * Get returning-session breakdown by industry / country / machine type
   */
  async getRetentionBreakdown(
    options: RetentionBreakdownOptions = {}
  ): Promise<RetentionBreakdownResponse> {
    const { env = 'prod', days, filters } = options;
    const params = new URLSearchParams();
    params.append('env', env);
    if (days) params.append('days', String(days));
    if (filters) {
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.userIds && filters.userIds.length > 0) {
        params.append('userIds', filters.userIds.join(','));
      }
      if (filters.excludeUserIds && filters.excludeUserIds.length > 0) {
        params.append('excludeUserIds', filters.excludeUserIds.join(','));
      }
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.excludeOrderfox) params.append('excludeOrderfox', 'true');
    }
    return this.request<RetentionBreakdownResponse>(
      `/v1/azure/retention-breakdown?${params.toString()}`
    );
  }

  /**
   * Get paginated Azure sessions with optional filtering
   * Unified endpoint that replaces getAzureLatestPaginated and getAzureLogsByTagsPaginated
   * @param options - Query options
   * @returns Paginated sessions response
   */
  async getAzureSessions(
    options: {
      env?: string;
      limit?: number;
      cursor?: string;
      sortDir?: 'asc' | 'desc';
      filters?: {
        user?: string;
        date?: string;
        dateFrom?: string; // Date range start (YYYY-MM-DD, inclusive)
        dateTo?: string; // Date range end (YYYY-MM-DD, inclusive)
        requestId?: string;
        session?: string;
        has_errors?: string;
        excludeUserIds?: string[]; // For excluding internal users
        tiers?: string[]; // Subscription tiers (multi-select)
        turnsOp?: string; // Turn count operator: eq | gt | gte | lt | lte
        turnsValue?: string; // Turn count value (non-negative integer)
        returningOnly?: string; // '1' restricts to the returning-session cohort; needs a date window
      };
    } = {}
  ): Promise<PaginatedSessionsResponse> {
    const { env, limit = 20, cursor, sortDir = 'desc', filters = {} } = options;

    const params = new URLSearchParams({
      limit: String(limit),
      sortDir,
    });

    // Add environment if present
    if (env) {
      params.append('env', env);
    }

    // Add cursor if present
    if (cursor) {
      params.append('cursor', cursor);
    }

    // Add filters if present (array filters are sent as comma-separated strings)
    for (const [key, value] of Object.entries(filters)) {
      if (Array.isArray(value)) {
        if (value.length > 0) params.append(key, value.join(','));
      } else if (typeof value === 'string' && value.trim().length > 0) {
        params.append(key, value);
      }
    }

    return this.request<PaginatedSessionsResponse>(`/v1/azure/logs/sessions?${params.toString()}`);
  }

  /**
   * Get turns with dashboards for a specific session
   * @param sessionId - Session ID to fetch dashboards for
   * @returns Session dashboards response with turns that have dashboard data
   */
  async getSessionDashboards(sessionId: string, env?: string): Promise<SessionDashboardsResponse> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<SessionDashboardsResponse>(
      `/v1/azure/logs/${encodeURIComponent(sessionId)}/dashboards?${params.toString()}`
    );
  }

  /**
   * Get Data Explorer datasets for a specific session
   * @param sessionId - Session ID to fetch datasets for
   */
  async getSessionDatasets(sessionId: string, env?: string): Promise<SessionDatasetsResponse> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<SessionDatasetsResponse>(
      `/v1/azure/logs/${encodeURIComponent(sessionId)}/datasets?${params.toString()}`
    );
  }

  /**
   * Get data exports (CSV/XLSX/CRM) for a specific session
   * @param sessionId - Session ID to fetch data exports for
   */
  async getSessionDataExports(
    sessionId: string,
    env?: string
  ): Promise<SessionDataExportsResponse> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<SessionDataExportsResponse>(
      `/v1/azure/logs/${encodeURIComponent(sessionId)}/data-exports?${params.toString()}`
    );
  }

  async getSessionReports(sessionId: string, env?: string): Promise<SessionReport[]> {
    const params = new URLSearchParams();
    params.append('sessionId', sessionId);
    if (env) params.append('env', env);
    return this.request<SessionReport[]>(`/v1/reports?${params.toString()}`);
  }

  async getSessionDashboardPresentations(
    sessionId: string,
    env?: string
  ): Promise<SessionDashboardPresentation[]> {
    const params = new URLSearchParams();
    params.append('sessionId', sessionId);
    if (env) params.append('env', env);
    return this.request<SessionDashboardPresentation[]>(
      `/v1/dashboard-presentations?${params.toString()}`
    );
  }

  /** Resolve a server-relative path (e.g. a proxy download URL) to an absolute URL. */
  resolveUrl(path: string): string {
    return /^https?:\/\//.test(path) ? path : `${API_BASE}${path}`;
  }

  /**
   * Get Clerk users for both development and production environments
   * @param options - Query options for filtering, pagination, and sorting
   * @returns Users from both Clerk environments
   */
  async getClerkUsers(
    options: {
      refresh?: boolean;
      activeOnly?: boolean;
      limit?: number;
      offset?: number;
      sortBy?: 'last_active_at' | 'created_at' | 'email';
      sortDir?: 'asc' | 'desc';
      search?: string;
      excludeInternal?: boolean;
      dateFrom?: string;
      dateTo?: string;
      billingPeriod?: 'monthly' | 'yearly';
      orgRole?: 'admin' | 'member';
      env?: DatabaseEnv;
    } = {}
  ): Promise<ClerkUsersResponse> {
    const {
      refresh = false,
      activeOnly = true,
      limit = 50,
      offset = 0,
      sortBy = 'last_active_at',
      sortDir = 'desc',
      search,
      excludeInternal,
      dateFrom,
      dateTo,
      billingPeriod,
      orgRole,
      env,
    } = options;
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    if (refresh) params.append('refresh', 'true');
    params.append('activeOnly', String(activeOnly));
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    params.append('sortBy', sortBy);
    params.append('sortDir', sortDir);
    if (search) params.append('search', search);
    if (excludeInternal) params.append('excludeInternal', 'true');
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    if (billingPeriod) params.append('billingPeriod', billingPeriod);
    if (orgRole) params.append('orgRole', orgRole);
    return this.request<ClerkUsersResponse>(`/v1/clerk/users?${params.toString()}`);
  }

  /**
   * Get details for a specific user by ID
   */
  async getClerkUser(userId: string, env?: string): Promise<ClerkUser> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<ClerkUser>(
      `/v1/clerk/users/${encodeURIComponent(userId)}?${params.toString()}`
    );
  }

  /**
   * Get all sessions for a specific user with their turns (user questions)
   * @param userId - User ID to fetch sessions for
   * @param options - Query options for environment and pagination
   * @returns User sessions with turns containing user messages
   */
  async getUserSessions(
    userId: string,
    options: { env?: string; limit?: number; cursor?: string } = {}
  ): Promise<UserSessionsResponse> {
    const { env, limit = 50, cursor } = options;
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    params.append('limit', String(limit));
    if (cursor) params.append('cursor', cursor);
    return this.request<UserSessionsResponse>(
      `/v1/clerk/users/${encodeURIComponent(userId)}/sessions?${params.toString()}`
    );
  }

  /**
   * Get profile for a specific user
   * @param userId - User ID to fetch profile for
   * @param env - Optional environment
   * @returns Profile or null
   */
  async getUserProfile(userId: string, env?: string): Promise<{ profile: UserProfile | null }> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<{ profile: UserProfile | null }>(
      `/v1/clerk/users/${encodeURIComponent(userId)}/profile?${params.toString()}`
    );
  }

  /**
   * Get mem0 memories for a specific user
   * @param userId - User ID to fetch memories for
   * @param options - Query options for environment, search, and pagination
   * @returns Mem0 memories response
   */
  async getUserMemories(
    userId: string,
    options?: { env?: string; search?: string; page?: number; pageSize?: number }
  ): Promise<Mem0MemoriesResponse> {
    const params = new URLSearchParams();
    if (options?.env) params.append('env', options.env);
    if (options?.search) params.append('search', options.search);
    if (options?.page) params.append('page', String(options.page));
    if (options?.pageSize) params.append('page_size', String(options.pageSize));
    return this.request<Mem0MemoriesResponse>(
      `/v1/clerk/users/${encodeURIComponent(userId)}/memories?${params.toString()}`
    );
  }

  /**
   * Get profiles for multiple users in batch
   * @param userIds - Array of user IDs to fetch profiles for
   * @param env - Optional environment
   * @returns Map of userId -> UserProfile
   */
  async getBatchProfiles(userIds: string[], env?: string): Promise<Record<string, UserProfile>> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<Record<string, UserProfile>>(
      `/v1/clerk/users/profiles/batch?${params.toString()}`,
      {
        method: 'POST',
        body: JSON.stringify({ userIds }),
      }
    );
  }

  /**
   * Get location data for a user from their most recent active session
   * @param userId - User ID to fetch location for
   * @returns User location data (city, country, device info) or null if no active session
   */
  async getUserLocation(userId: string, env?: DatabaseEnv): Promise<UserLocation | null> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<UserLocation | null>(
      `/v1/clerk/users/${encodeURIComponent(userId)}/location?${params.toString()}`
    );
  }

  // ============================================================================
  // Report Wizards
  // ============================================================================

  async getReportWizards(
    options: {
      env?: string;
      limit?: number;
      cursor?: string;
      sortDir?: 'asc' | 'desc';
      filters?: {
        user?: string;
        status?: string;
        date?: string;
        excludeUserIds?: string[];
      };
    } = {}
  ): Promise<PaginatedReportWizardsResponse> {
    const { env, limit = 20, cursor, sortDir = 'desc', filters = {} } = options;

    const params = new URLSearchParams({ limit: String(limit), sortDir });
    if (env) params.append('env', env);
    if (cursor) params.append('cursor', cursor);

    for (const [key, value] of Object.entries(filters)) {
      if (key === 'excludeUserIds' && Array.isArray(value) && value.length > 0) {
        params.append('excludeUserIds', value.join(','));
      } else if (typeof value === 'string' && value.trim().length > 0) {
        params.append(key, value);
      }
    }

    return this.request<PaginatedReportWizardsResponse>(
      `/v1/azure/report-wizards?${params.toString()}`
    );
  }

  // ============================================================================
  // V2 Judge Results
  // ============================================================================

  async getSessionJudgeResultsV2(
    sessionId: string,
    env?: string
  ): Promise<{ judgeResults: JudgeResultData[] }> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    const queryString = params.toString();
    try {
      return await this.request<{ judgeResults: JudgeResultData[] }>(
        `/v1/azure/logs/${encodeURIComponent(sessionId)}/judge-results-v2${queryString ? `?${queryString}` : ''}`
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return { judgeResults: [] };
      }
      throw error;
    }
  }

  async runSessionEvaluateV2(
    sessionId: string,
    env?: string,
    overwrite?: boolean
  ): Promise<{ judgeResults: JudgeResultData[]; skippedJudges?: JudgeSkipReason[] }> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    if (overwrite) params.append('overwrite', 'true');
    const queryString = params.toString();
    return this.request<{ judgeResults: JudgeResultData[]; skippedJudges?: JudgeSkipReason[] }>(
      `/v1/azure/logs/${encodeURIComponent(sessionId)}/evaluate-v2${queryString ? `?${queryString}` : ''}`,
      {
        method: 'POST',
        timeoutMs: 300000,
      }
    );
  }

  // ============================================================================
  // Report Pipeline Jobs (gieni-reports Azure Function proxy)
  // ============================================================================

  async getReportPipelineJobs(
    options: {
      env?: string;
      limit?: number;
      offset?: number;
      status?: string;
      start_time?: string;
      end_time?: string;
    } = {}
  ): Promise<ReportPipelineJobsResponse> {
    const { env, limit, offset, status, start_time, end_time } = options;
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    if (limit) params.append('limit', String(limit));
    if (offset) params.append('offset', String(offset));
    if (status) params.append('status', status);
    if (start_time) params.append('start_time', start_time);
    if (end_time) params.append('end_time', end_time);
    return this.request<ReportPipelineJobsResponse>(
      `/v1/report-pipeline/jobs?${params.toString()}`,
      { timeoutMs: 30000 }
    );
  }

  async getReportPipelineJob(jobId: string, env?: string): Promise<ReportPipelineJob> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<ReportPipelineJob>(
      `/v1/report-pipeline/jobs/${encodeURIComponent(jobId)}?${params.toString()}`,
      { timeoutMs: 30000 }
    );
  }

  async getReportPipelineJobParams(jobId: string, env?: string): Promise<unknown> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<unknown>(
      `/v1/report-pipeline/jobs/${encodeURIComponent(jobId)}/params?${params.toString()}`,
      { timeoutMs: 30000 }
    );
  }

  async retryReportPipelineJob(jobId: string, env?: string): Promise<unknown> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<unknown>(
      `/v1/report-pipeline/jobs/${encodeURIComponent(jobId)}/retry?${params.toString()}`,
      { method: 'POST', timeoutMs: 60000 }
    );
  }

  async stopReportPipelineJob(jobId: string, env?: string): Promise<unknown> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<unknown>(
      `/v1/report-pipeline/jobs/${encodeURIComponent(jobId)}/stop?${params.toString()}`,
      { method: 'POST', timeoutMs: 30000 }
    );
  }

  async getReportWizard(id: string, env?: string): Promise<ReportWizardDetail> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<ReportWizardDetail>(
      `/v1/azure/report-wizards/${encodeURIComponent(id)}?${params.toString()}`
    );
  }

  // ---------------------------------------------------------------------------
  // Credit Analytics
  // ---------------------------------------------------------------------------

  async getCreditUsageSummary(
    userId: string,
    env?: string,
    from?: string,
    to?: string
  ): Promise<CreditUsageSummary> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<CreditUsageSummary>(
      `/v1/credit-analytics/usage-summary?${params.toString()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, from, to }),
      }
    );
  }

  async getCreditEventHistory(
    userId: string,
    env?: string,
    options?: { eventTypes?: string[]; from?: string; to?: string; limit?: number; offset?: number }
  ): Promise<CreditEventHistory> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<CreditEventHistory>(
      `/v1/credit-analytics/event-history?${params.toString()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, ...options }),
      }
    );
  }

  async getBulkCreditUsageSummary(
    env?: string,
    options?: { userIds?: string[]; from?: string; to?: string }
  ): Promise<BulkCreditUsageEntry[]> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<BulkCreditUsageEntry[]>(
      `/v1/credit-analytics/bulk-usage-summary?${params.toString()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options ?? {}),
      }
    );
  }

  async getCreditPlanHistory(userId: string, env?: string): Promise<PlanHistoryEntry[]> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    return this.request<PlanHistoryEntry[]>(
      `/v1/credit-analytics/plan-history?${params.toString()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      }
    );
  }

  async getUsersBillingInfo(
    userIds: string[],
    env?: string
  ): Promise<Record<string, UserBillingInfo>> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    const result = await this.request<{ users: Record<string, UserBillingInfo> }>(
      `/v1/azure/users/billing?${params.toString()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds }),
      }
    );
    return result.users;
  }

  /**
   * Get billing details for a single user. Unlike the batch endpoint, the
   * response includes the billing period and the user's organization role.
   * Returns null when no billing data is available for the user.
   */
  async getUserBillingDetails(userId: string, env?: string): Promise<UserBillingDetails | null> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    try {
      const result = await this.request<{ user: UserBillingDetails }>(
        `/v1/azure/users/${encodeURIComponent(userId)}/billing?${params.toString()}`
      );
      return result.user;
    } catch {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Stripe Subscriptions
  // ---------------------------------------------------------------------------

  async getStripeOverview(env?: DatabaseEnv): Promise<StripeSubscriptionOverview> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    const queryString = params.toString();
    return this.request<StripeSubscriptionOverview>(
      `/v1/stripe/overview${queryString ? `?${queryString}` : ''}`
    );
  }

  async getStripeChanges(limit = 20, env?: DatabaseEnv): Promise<StripeRecentChange[]> {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (env) params.append('env', env);
    return this.request<StripeRecentChange[]>(`/v1/stripe/changes?${params.toString()}`);
  }

  async getStripeUserSubscriptions(
    email: string,
    env?: DatabaseEnv
  ): Promise<StripeUserSubscription[]> {
    const params = new URLSearchParams();
    if (env) params.append('env', env);
    const queryString = params.toString();
    return this.request<StripeUserSubscription[]>(
      `/v1/stripe/user/${encodeURIComponent(email)}${queryString ? `?${queryString}` : ''}`
    );
  }

  // ---------------------------------------------------------------------------
  // PostHog Event Analytics (P4)
  // ---------------------------------------------------------------------------

  async getPosthogEvents(
    days = 30,
    userIds?: string[],
    environment?: PosthogEnvironment,
    excludeUserIds?: string[]
  ): Promise<PosthogEventOverview> {
    const params = new URLSearchParams({ days: String(days) });
    if (userIds?.length) params.set('userIds', userIds.join(','));
    if (environment) params.set('environment', environment);
    if (excludeUserIds?.length) params.set('excludeUserIds', excludeUserIds.join(','));
    return this.request<PosthogEventOverview>(`/v1/posthog/events?${params}`);
  }

  async getPosthogActions(
    days = 30,
    userIds?: string[],
    environment?: PosthogEnvironment,
    excludeUserIds?: string[]
  ): Promise<PosthogEventOverview> {
    const params = new URLSearchParams({ days: String(days) });
    if (userIds?.length) params.set('userIds', userIds.join(','));
    if (environment) params.set('environment', environment);
    if (excludeUserIds?.length) params.set('excludeUserIds', excludeUserIds.join(','));
    return this.request<PosthogEventOverview>(`/v1/posthog/actions?${params}`);
  }

  async getPosthogTopUsers(
    days = 30,
    limit = 20,
    userIds?: string[],
    environment?: PosthogEnvironment,
    excludeUserIds?: string[]
  ): Promise<PosthogUserEventSummary[]> {
    const params = new URLSearchParams({ days: String(days), limit: String(limit) });
    if (userIds?.length) params.set('userIds', userIds.join(','));
    if (environment) params.set('environment', environment);
    if (excludeUserIds?.length) params.set('excludeUserIds', excludeUserIds.join(','));
    return this.request<PosthogUserEventSummary[]>(`/v1/posthog/top-users?${params}`);
  }

  async getPosthogDailyActivity(
    days = 30,
    userIds?: string[],
    environment?: PosthogEnvironment,
    excludeUserIds?: string[]
  ): Promise<PosthogDailyUserActivity[]> {
    const params = new URLSearchParams({ days: String(days) });
    if (userIds?.length) params.set('userIds', userIds.join(','));
    if (environment) params.set('environment', environment);
    if (excludeUserIds?.length) params.set('excludeUserIds', excludeUserIds.join(','));
    return this.request<PosthogDailyUserActivity[]>(`/v1/posthog/daily-activity?${params}`);
  }

  async getPosthogChatModes(
    days = 30,
    userIds?: string[],
    environment?: PosthogEnvironment,
    excludeUserIds?: string[]
  ): Promise<PosthogChatModeOverview> {
    const params = new URLSearchParams({ days: String(days) });
    if (userIds?.length) params.set('userIds', userIds.join(','));
    if (environment) params.set('environment', environment);
    if (excludeUserIds?.length) params.set('excludeUserIds', excludeUserIds.join(','));
    return this.request<PosthogChatModeOverview>(`/v1/posthog/chat-modes?${params}`);
  }

  async translateTexts(texts: string[]): Promise<string[]> {
    const result = await this.request<{ translations: string[] }>('/v1/translate', {
      method: 'POST',
      body: JSON.stringify({ texts }),
      timeoutMs: 120000,
    });
    return result.translations;
  }

  async translateSingle(text: string): Promise<string> {
    const result = await this.request<{ translations: string[] }>('/v1/translate', {
      method: 'POST',
      body: JSON.stringify({ texts: [text] }),
      timeoutMs: 90000,
    });
    return result.translations[0];
  }
}

export const apiService = new ApiService();
export { ApiError };
